// Common ID types available for ALL countries
export const UNIVERSAL_ID_TYPES = [
  { id: 'passport', label: 'Passport' },
  { id: 'drivers_licence', label: "Driver's Licence" },
]

// Country-specific ID types — keyed by ISO alpha-2 code
export const COUNTRY_ID_TYPES: Record<string, Array<{ id: string; label: string; note?: string }>> = {
  AU: [
    { id: 'au_medicare', label: 'Medicare Card' },
    { id: 'au_birth_cert', label: 'Birth Certificate' },
    { id: 'au_citizenship', label: 'Citizenship Certificate' },
    { id: 'au_immicard', label: 'ImmiCard' },
    { id: 'au_photo_id', label: 'State Photo ID Card' },
  ],
  NZ: [
    { id: 'nz_birth_cert', label: 'Birth Certificate' },
    { id: 'nz_citizenship', label: 'Citizenship Certificate' },
    { id: 'nz_firearms', label: 'Firearms Licence' },
  ],
  US: [
    { id: 'us_ssn', label: 'Social Security Number (SSN)' },
    { id: 'us_state_id', label: 'State ID Card' },
    { id: 'us_green_card', label: 'Green Card (Permanent Resident)' },
    { id: 'us_military_id', label: 'Military ID' },
  ],
  SG: [
    { id: 'sg_nric', label: 'NRIC (National Registration Identity Card)' },
    { id: 'sg_fin', label: 'FIN (Foreign Identification Number)' },
  ],
  JP: [
    { id: 'jp_my_number', label: 'My Number Card (Individual Number)' },
    { id: 'jp_residence', label: 'Residence Card (Zairyū Card)' },
  ],
  BR: [
    { id: 'br_cpf', label: 'CPF (Cadastro de Pessoas Físicas)' },
    { id: 'br_rg', label: 'RG (Registro Geral)' },
  ],
  CO: [
    { id: 'co_cedula', label: 'Cédula de Ciudadanía', note: 'Required for reliable matching in Colombia' },
    { id: 'co_cedula_ext', label: 'Cédula de Extranjería', note: 'For foreign nationals' },
  ],
  MX: [
    { id: 'mx_curp', label: 'CURP' },
    { id: 'mx_ine', label: 'INE Voter ID' },
  ],
  ZA: [
    { id: 'za_id_card', label: 'South African ID Card / Smart ID' },
  ],
  NG: [
    { id: 'ng_nin', label: 'National Identification Number (NIN)' },
    { id: 'ng_bvn', label: 'Bank Verification Number (BVN)' },
    { id: 'ng_voter', label: 'Voter Card' },
  ],
  KE: [
    { id: 'ke_national_id', label: 'National ID Card (Huduma Namba)' },
  ],
  CN: [
    { id: 'cn_resident_id', label: 'Resident Identity Card' },
  ],
  ID: [
    { id: 'id_ktp', label: 'Kartu Tanda Penduduk (KTP)' },
  ],
  IT: [
    { id: 'it_cie', label: "Carta d'Identità Elettronica (CIE)" },
  ],
  VN: [
    { id: 'vn_identity_card', label: 'Identity Card (Căn cước)' },
  ],
  TH: [
    { id: 'th_identity_card', label: 'Thai Identity Card' },
  ],
  MY: [
    { id: 'my_mykad', label: 'MyKad (citizens)' },
    { id: 'my_mypr', label: 'MyPR (permanent residents)' },
    { id: 'my_mytentera', label: 'MyTentera (military)' },
    { id: 'my_mypolis', label: 'MyPolis (police)' },
  ],
  GB: [
    { id: 'gb_brp', label: 'Biometric Residence Permit', note: 'UK has no single national ID card' },
  ],
  TW: [
    { id: 'tw_national_id', label: 'National Identification Card' },
  ],
  IN: [
    { id: 'in_aadhaar', label: 'Aadhaar Card' },
    { id: 'in_voter_id', label: 'Voter ID Card' },
    { id: 'in_pan', label: 'PAN Card' },
  ],
  PH: [
    { id: 'ph_philid', label: 'PhilSys ID (PhilID)' },
  ],
  CA: [
    { id: 'ca_provincial_id', label: 'Provincial/Territorial ID Card', note: 'Canada has no national ID card' },
  ],
  BE: [
    { id: 'be_eid', label: 'Belgian Identity Card (eID)' },
  ],
  HK: [
    { id: 'hk_hkic', label: 'Hong Kong Identity Card (HKIC)' },
  ],
  KR: [
    { id: 'kr_rrc', label: 'Resident Registration Card' },
  ],
  FR: [
    { id: 'fr_cni', label: "Carte Nationale d'Identité (CNI)" },
  ],
  DE: [
    { id: 'de_personalausweis', label: 'Personalausweis (Identity Card)' },
  ],
  AE: [
    { id: 'ae_emirates_id', label: 'Emirates ID' },
  ],
  PK: [
    { id: 'pk_cnic', label: 'Computerized National Identity Card (CNIC)' },
  ],
  ES: [
    { id: 'es_dni', label: 'Documento Nacional de Identidad (DNI)' },
  ],
  NL: [
    { id: 'nl_id_card', label: 'Nederlandse Identiteitskaart (Dutch Identity Card)' },
  ],
}

// Countries that have predefined ID types (beyond universal)
export const COUNTRIES_WITH_ID_TYPES = new Set(Object.keys(COUNTRY_ID_TYPES))

/**
 * Get all available ID type options for a country.
 * Returns universal types + country-specific types.
 * Returns null if the country isn't in our list (use free-text fallback).
 */
export function getIdTypesForCountry(countryCode: string): Array<{ id: string; label: string; note?: string }> | null {
  const specific = COUNTRY_ID_TYPES[countryCode.toUpperCase()]
  if (!specific) return null // Country not in our list — use free-text
  return [...UNIVERSAL_ID_TYPES, ...specific]
}
